import { ChevronLeft, ChevronRight, SearchX, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { hitToCardData, StockCarCardView } from './StockCarCard';

import type { StockSearchHit } from '@/lib/api/dealer-kit.api';

interface Props {
  hits: StockSearchHit[] | undefined;
  page: number;
  totalPages: number;
  loading: boolean;
  sourceLabel: (hit: StockSearchHit) => string;
  onPage: (page: number) => void;
  emptyActions: { label: string; onClick: () => void }[];
  isError?: boolean;
  onRetry?: () => void;
}

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

/** 1 … 4 5 [6] 7 8 … 20 */
const pageList = (page: number, total: number): (number | 'gap')[] => {
  const pages = new Set([1, total, page - 1, page, page + 1].filter((p) => p >= 1 && p <= total));
  const sorted = [...pages].sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? (['gap', p] as const) : [p]));
};

export const StockResultsGrid = ({
  hits,
  page,
  totalPages,
  loading,
  sourceLabel,
  onPage,
  emptyActions,
  isError = false,
  onRetry,
}: Props) => {
  if (loading && !hits) {
    return (
      <div className={GRID} aria-busy="true">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="overflow-hidden rounded-lg border">
            <Skeleton className="aspect-[16/10] w-full rounded-none" />
            <div className="space-y-2 p-3">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!hits?.length && isError) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center"
      >
        <TriangleAlert className="text-destructive h-8 w-8" aria-hidden />
        <p className="font-medium">Search failed — try again</p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (!hits?.length) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center">
        <SearchX className="text-muted-foreground h-8 w-8" aria-hidden />
        <p className="font-medium">No cars match these filters</p>
        {emptyActions.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-2">
            <span className="text-muted-foreground text-sm">Try:</span>
            {emptyActions.map((a) => (
              <Button key={a.label} size="sm" variant="outline" onClick={a.onClick}>
                {a.label}
              </Button>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            Try different words, or ask the AI for something similar.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`${GRID} transition-opacity ${loading ? 'opacity-60' : ''}`}>
        {hits.map((hit) => (
          <StockCarCardView key={hit.id} car={hitToCardData(hit, sourceLabel(hit))} />
        ))}
      </div>
      {totalPages > 1 && (
        <nav aria-label="Results pages" className="flex items-center justify-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          {pageList(page, totalPages).map((p, i) =>
            p === 'gap' ? (
              <span key={`gap-${i}`} className="text-muted-foreground px-2">
                …
              </span>
            ) : (
              <Button
                key={p}
                size="sm"
                variant={p === page ? 'default' : 'ghost'}
                aria-current={p === page ? 'page' : undefined}
                onClick={() => onPage(p)}
              >
                {p}
              </Button>
            ),
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPage(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </nav>
      )}
    </div>
  );
};
